import { IActivityHealth } from "./contracts/types";
import { NEVER_SUCCEEDED, WorstDelay } from "./aggregate";

/**
 * Formatação compartilhada do painel. Criado no T9 (plano v2, 2026-08-11)
 * porque `formatFederalTaxId` vivia solto em `CnpjPage.tsx` e a lista do
 * portfólio passou a precisar do mesmo formato — e um segundo formatador
 * copiado seria a maneira mais fácil das duas telas divergirem.
 *
 * Confirmado antes de criar (instrução da T9): não há formatador de CNPJ
 * compartilhado no app. `src/utils/is-valid-cnpj.ts` valida, não formata.
 */

/** `48785828009428` -> `48.785.828/0094-28`. Tolera valores curtos preenchendo à esquerda. */
export function formatFederalTaxId(value: string): string {
  const digits = value.padStart(14, "0");
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
}

/**
 * Renderiza o pior atraso de um CNPJ/workspace.
 *
 * "nunca" é texto, não número: é o pior caso possível e não pode ser
 * confundido com um valor alto de dias nem com a ausência de dado (T3).
 */
export function formatWorstDelay(delay: WorstDelay): string | null {
  if (delay === NEVER_SUCCEEDED) return "nunca";
  if (delay === undefined) return null;
  if (delay === 0) return "hoje";
  return `${delay} dia${delay > 1 ? "s" : ""}`;
}

/** `2026-07-01T14:01:00Z` -> `01/07`. Só dia e mês: o ano polui a linha e quase nunca importa. */
export function formatShortDate(iso: string): string {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "America/Sao_Paulo",
  }).format(new Date(iso));
}

/**
 * T8 (plano v2, 2026-08-11) — cada estado se explica no seu próprio
 * vocabulário.
 *
 * O defeito que isto corrige: a linha imprimia `recentFailureRate` para
 * qualquer estado, porque era o dado que o componente tinha à mão. Numa
 * atividade `PAROU` isso produzia `Sincronização API — 0 falhas em 6 · há 41
 * dias` com badge de alerta: a frase acusa e anuncia zero falhas ao mesmo
 * tempo. Para uma atividade parada a contagem de falhas é irrelevante por
 * construção — nada falhou porque nada rodou.
 *
 * `PAROU` continua no rollup e continua acionável (D6): muda a frase, não o
 * veredito.
 */
export function describeActivity(activity: IActivityHealth): string {
  const { activityName, status } = activity;

  if (status === "PAROU") {
    const since = activity.lastExecutionAt ? `parada desde ${formatShortDate(activity.lastExecutionAt)}` : "parada";
    const elapsed = activity.lastExecutionAt ? daysSince(activity.lastExecutionAt) : null;
    const expectation = activity.expectedIntervalDays
      ? `, esperada a cada ~${formatInterval(activity.expectedIntervalDays)}`
      : "";

    return `${activityName} — ${since}${elapsed !== null ? ` · ${elapsed} dias` : ""}${expectation}`;
  }

  const failures = activity.recentFailureRate
    ? ` — ${activity.recentFailureRate.failures} falhas em ${activity.recentFailureRate.total}`
    : "";

  if (activity.neverSucceeded) return `${activityName}${failures} · nunca teve sucesso`;

  const lag = activity.daysSinceLastSuccess ? ` · há ${activity.daysSinceLastSuccess} dias` : "";
  return `${activityName}${failures}${lag}`;
}

/** Métricas do QBem na linha (T4b): "1.240 registros · 38 inconsistentes · 1 erro crítico". */
export function describeQbem(activity: IActivityHealth): string | null {
  const { qbem } = activity;
  if (!qbem) return null;

  const parts: string[] = [`${qbem.totalRecords.toLocaleString("pt-BR")} registros`];

  if (qbem.inconsistentRecords > 0) {
    parts.push(`${qbem.inconsistentRecords.toLocaleString("pt-BR")} inconsistentes`);
  }

  const criticalCount = qbem.criticalErrors?.length ?? 0;
  if (criticalCount > 0) {
    parts.push(`${criticalCount} erro${criticalCount > 1 ? "s" : ""} crítico${criticalCount > 1 ? "s" : ""}`);
  }

  if (qbem.reason === "RETENTIONS" && qbem.retainedCount) {
    parts.push(`${qbem.retainedCount.toLocaleString("pt-BR")} retidos`);
  }

  return parts.join(" · ");
}

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000));
}

function formatInterval(days: number): string {
  return days === 1 ? "1 dia" : `${days.toLocaleString("pt-BR")} dias`;
}
